import React, { useEffect } from "react";
import { useQuery } from "@apollo/client";
import dynamic from "next/dynamic";
import {
  GET_AUTH_BIDDING_DETAILS,
  GET_BIDDING_DETAILS,
} from "@/graphql/queries/listingAuction";
import style from "../.module.scss";
import { financialFormat } from "@/lib/helpers";
import { getDiffTimestamps } from "@/lib/dateHelpers";
import { useAuthState } from "@/contexts/auth/auth.provider";
import BiddingActionBar from "./BiddingActionBar";
import { Icon } from "@/components/icon";

const BiddingForm = dynamic(() => import("./BiddingForm"), {
  ssr: false,
});

export function StickyContainer({ children, className }) {
  const nodRef = React.useRef();
  useEffect(() => {
    const handleScroll = () => {
      if (!nodRef?.current) return;
      const topPosition = nodRef.current.getBoundingClientRect().top;

      if (topPosition <= 0) {
        nodRef.current.classList.add(className);
      } else {
        nodRef.current.classList.remove(className);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return React.cloneElement(children, {
    ref: nodRef,
  });
}

const BiddingDetails = ({
  listingId,
  isListingOwner,
  bidMode,
  setListingBidId,
}) => {
  const { token } = useAuthState();
  const biddingDetailsQuery = token
    ? GET_AUTH_BIDDING_DETAILS
    : GET_BIDDING_DETAILS;

  const { data: biddingDetails, loading: biddingDetailsLoading } = useQuery(
    biddingDetailsQuery,
    {
      context: { token },
      variables: { listing_id: listingId },
    },
  );

  const {
    min_offer_amount: minOfferAmount,
    current_number_of_bidders: offersNumber,
    sale_start_date: startDate,
    sale_end_date: endDate,
    listing_bidding_id: listingBidId,
    participation_fee: participationFees,
    user_offer: userOffer,
    status,
  } = biddingDetails?.ListingAuction?.getBiddingDetails || {};

  useEffect(() => {
    setListingBidId(listingBidId);
  }, [listingBidId]);

  return (
    <>
      <div className={style.card}>
        <div className={style.biddingDetails}>
          <div className={style.topSection}>
            <Icon
              alt="عقار"
              name="lightning-circle-yellow"
              width={45}
              height={45}
            />
            <div className={style.topSectionTitle}>البيع السريع</div>
            <div className={style.topSectionSubtitle}>
              بإمكانك تقديم عرض شراء مباشرة للمعلن خلال مدة العرض.
            </div>
          </div>
          <div className={style.bottomSection}>
            <div className={style.biddingDetailsItem}>
              <Icon alt="عقار" name="money" width={30} height={30} />
              <div className={style.title}>السعر الأدنى</div>
              <div className={style.value}>
                {biddingDetailsLoading
                  ? ""
                  : financialFormat(minOfferAmount || "", "ريال")}
              </div>
            </div>
            <div className={style.biddingDetailsItem}>
              <Icon alt="عقار" name="user-chat" width={30} height={30} />
              <div className={style.title}>عدد العروض</div>
              <div className={style.value}>
                {biddingDetailsLoading ? "" : offersNumber || 0}
              </div>
            </div>
            <div className={style.biddingDetailsItem}>
              <Icon alt="عقار" name="clock-circle" width={30} height={30} />
              <div className={style.title}>مدة العرض</div>
              <div className={style.value}>
                {biddingDetailsLoading
                  ? ""
                  : getDiffTimestamps(startDate, endDate)}
              </div>
            </div>
          </div>
        </div>
      </div>
      {bidMode === "off" && (
        <BiddingActionBar
          endDate={endDate}
          isListingOwner={isListingOwner}
          hasPrevBid={!!userOffer}
          status={status}
        />
      )}
      {bidMode === "bid" && (
        <BiddingForm
          listingBidId={listingBidId}
          participationFees={participationFees}
          prevOfferAmount={userOffer?.offer_amount}
        />
      )}
    </>
  );
};

BiddingDetails.propTypes = {};

export default BiddingDetails;
